import React from 'react';
import Modal from 'react-modal';
import { useLocation, useNavigate } from 'react-router-dom';

import { getNextWindowZIndex } from 'utils/windowZIndex';

import { ColorPickerDragLockProvider } from './colorPickerDragLock';
import { SURVEY_DATA_VIEWER_PATH } from './constants';
import SurveyDataManagerPanel from './SurveyDataManagerPanel';
import './style.scss';

export { SURVEY_DATA_VIEWER_MODAL_TITLE, SURVEY_DATA_VIEWER_PATH } from './constants';

type RefreshState =
  | { readonly status: 'idle'; readonly requestId: number }
  | { readonly status: 'pending'; readonly requestId: number };

function SurveyDataManager(): JSX.Element | null {
  const location = useLocation();
  const navigate = useNavigate();
  const [isModalMinimized, setModalMinimized] = React.useState<boolean>(false);
  const [zIndex, setZIndex] = React.useState(20);
  const [refreshState, setRefreshState] = React.useState<RefreshState>({
    status: 'idle',
    requestId: 0,
  });

  const isOpen = location.pathname.startsWith(SURVEY_DATA_VIEWER_PATH);
  const isRefreshPending = refreshState.status === 'pending';

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    setModalMinimized(false);
  }, [isOpen]);

  // Bring SDM to the front when opened, and again whenever the user clicks it.
  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    setZIndex(getNextWindowZIndex());

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (!target.closest('.survey-data-viewer-modal')) {
        return;
      }
      setZIndex(getNextWindowZIndex());
    };

    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [isOpen]);

  const handleClose = () => {
    setRefreshState((currentRefreshState) => ({
      status: 'idle',
      requestId: currentRefreshState.requestId,
    }));
    navigate('/');
  };

  const handleRefresh = () => {
    if (isRefreshPending) {
      return;
    }

    setRefreshState((currentRefreshState) => ({
      status: 'pending',
      requestId: currentRefreshState.requestId + 1,
    }));
  };

  const handleRefreshSettled = () => {
    setRefreshState((currentRefreshState) => ({
      status: 'idle',
      requestId: currentRefreshState.requestId,
    }));
  };

  const toggleModalState = () => {
    setModalMinimized((prevState) => !prevState);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      ariaHideApp={false}
      isOpen
      onRequestClose={handleClose}
      bodyOpenClassName={null}
      portalClassName="react-modal-container"
      overlayClassName="react-modal-overlay"
      className="react-modal-wrapper"
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
    >
      <ColorPickerDragLockProvider>
        <SurveyDataManagerPanel
          isModalMinimized={isModalMinimized}
          isRefreshPending={isRefreshPending}
          refreshRequestId={refreshState.requestId}
          zIndex={zIndex}
          onClose={handleClose}
          onRefresh={handleRefresh}
          onRefreshSettled={handleRefreshSettled}
          onToggleMinimized={toggleModalState}
        />
      </ColorPickerDragLockProvider>
    </Modal>
  );
}

export default SurveyDataManager;
